<template>
  <Modal ref="modalRef" width="30rem">
    <template #header>Invite a teammate</template>

    <template #body>
      <View gap="lg" direction="column">
        <View direction="column" gap="sm">
          <FormField label="Email" :error="form.fieldErrors('email')">
            <Input
              v-model="form.fields.email"
              type="email"
              placeholder="teammate@company.com"
              data-testid="team-invite-email-input"
            >
              <template #prefix>
                <Icon class="email-icon" name="mail-01" size="small" />
              </template>
            </Input>
          </FormField>

          <View v-if="pendingInviteId" gap="sm" align="center" wrap="wrap">
            <Text variant="body-small" color="secondary">Their invite is still pending.</Text>
            <Button variant="outline" size="small" :loading="isResending" @click="resendPending">Resend instead</Button>
          </View>
        </View>

        <View direction="column" gap="sm">
          <Text variant="label-medium">Role</Text>
          <View direction="column" gap="sm">
            <RadioCard
              v-for="role in roles"
              :key="role"
              v-model="form.fields.role"
              name="team-invite-role"
              :value="role"
            >
              <View direction="column" gap="xs">
                <Text variant="label-medium">{{ companyRoleLabels[role] }}</Text>
                <Text variant="body-small" color="secondary">{{ companyRoleDescriptions[role] }}</Text>
              </View>
            </RadioCard>
          </View>
        </View>

        <View v-if="form.fields.role === CompanyRole.MEMBER && portfolios.length" direction="column" gap="sm">
          <View direction="column" gap="xs">
            <Text variant="label-medium">Portfolios to work on (optional)</Text>
            <Text variant="body-small" color="secondary">
              They join each one as a collaborator when they accept: every property in it, and the ones added later.
            </Text>
          </View>
          <PortfolioChecklist v-model="collaboratePortfolioIds" :portfolios="portfolios" />
        </View>

        <Hint v-if="form.fields.role === CompanyRole.MEMBER && !collaboratePortfolioIds.length" icon="info-circle">
          To let them change a property after they join, make them its manager or a collaborator on its Team tab, or
          give them a portfolio.
        </Hint>
      </View>
    </template>

    <template #footer>
      <View direction="column" gap="sm" class="footer">
        <Text variant="body-small" color="secondary">The link in the email works for 7 days.</Text>

        <View gap="sm" justify="end">
          <Button variant="ghost" @click="close">Cancel</Button>
          <Button :loading="form.submitting" data-testid="team-invite-send-button" @click="send">Send invite</Button>
        </View>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { email as emailValidator, helpers, required } from '@vuelidate/validators';
import { ref } from 'vue';

import {
  Button,
  FormField,
  Hint,
  Icon,
  Input,
  Modal,
  RadioCard,
  Text,
  View,
  readableServerMessage,
  serverErrorCode,
  serverErrorDetail,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import {
  CompanyRole,
  alreadyInvitedErrorCode,
  alreadyMemberErrorCode,
  companyRoleDescriptions,
  companyRoleLabels,
  emailTakenErrorCode,
  useCompanyPortfoliosQuery,
  useInviteMember,
  useResendInvite,
} from '@tallo/investor/company';
import { escapeHtml } from '@tallo/utility';

import PortfolioChecklist from './portfolio-checklist.vue';

const modalRef = ref<InstanceType<typeof Modal>>();
const alertsStore = useAlertsStore();
const inviteMember = useInviteMember();
const resendInvite = useResendInvite();
const { portfolios } = useCompanyPortfoliosQuery();

const roles = [CompanyRole.MEMBER, CompanyRole.ADMIN];

/** Set when the backend answered "already invited", so the form can offer the resend right there */
const pendingInviteId = ref<string | null>(null);
const isResending = ref(false);
const collaboratePortfolioIds = ref<string[]>([]);

const form = useForm<{ email: string; role: CompanyRole }>(
  { email: '', role: CompanyRole.MEMBER },
  {
    email: {
      required: helpers.withMessage('Email is required', required),
      email: helpers.withMessage('Email is not valid', emailValidator),
    },
  },
);

async function send() {
  pendingInviteId.value = null;

  try {
    await form.submit(async () => {
      const email = form.fields.email.trim();

      try {
        // An admin works every property already, so portfolios only mean something for a member
        await inviteMember.mutateAsync({
          email,
          role: form.fields.role,
          collaboratePortfolioIds: form.fields.role === CompanyRole.MEMBER ? collaboratePortfolioIds.value : [],
        });
        close();
        alertsStore.success(`Invite sent to <b>${escapeHtml(email)}</b>`);
      } catch (error) {
        const code = serverErrorCode(error);

        if (code === alreadyInvitedErrorCode) {
          const inviteId = serverErrorDetail(error, 'inviteId');
          pendingInviteId.value = typeof inviteId === 'string' ? inviteId : null;
        }

        if (code === alreadyInvitedErrorCode || code === alreadyMemberErrorCode || code === emailTakenErrorCode) {
          form.setFieldError('email', readableServerMessage(error) ?? 'This email cannot be invited');
          return;
        }

        alertsStore.catchError(error);
      }
    });
  } catch {
    // An invalid form shows its field errors; there is nothing else to report
  }
}

async function resendPending() {
  if (!pendingInviteId.value) return;

  isResending.value = true;

  try {
    await resendInvite.mutateAsync(pendingInviteId.value);
    const email = form.fields.email.trim();
    close();
    alertsStore.success(`Invite resent to <b>${escapeHtml(email)}</b>`);
  } catch {
    // Already reported by the mutation's toast; the form stays open for another try
  } finally {
    isResending.value = false;
  }
}

// A fresh form on every open rather than a reset on close: a reset that runs when the closing
// animation ends would clobber an open that came in during it
function open() {
  Object.assign(form.fields, { email: '', role: CompanyRole.MEMBER });
  collaboratePortfolioIds.value = [];
  pendingInviteId.value = null;
  form.resetValidation();
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

defineExpose({ open });
</script>

<style lang="scss" scoped>
.email-icon {
  color: var(--t-text-color-tertiary);
}

.footer {
  width: 100%;
}
</style>
